import React, { useState } from 'react';
import Badge from '@amzn/awsui-components-console/badge';
import Box from '@amzn/awsui-components-console/box';
import Container from '@amzn/awsui-components-console/container';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import { TranslationProgress } from '../types';
import styles from '../StringGroupDetail.module.scss';
import { TranslateFn, useTranslation } from 'src/i18n/shim/useTranslation';

interface TranslationProgressPanelProps {
  progress: TranslationProgress;
}

/** A status group with its locales, in display order. */
type LocaleStatusColor = 'green' | 'blue' | 'grey' | 'red';
interface LocaleStatusGroup {
  label: string;
  color: LocaleStatusColor;
  locales: string[];
}

/**
 * Groups the locales by status for the details view. One line per status
 * (rather than one row per locale) keeps the panel compact when a group has
 * many locales — the previous per-locale table scrolled arbitrarily long.
 * Empty groups are dropped so only statuses that actually have locales show.
 * Cancelled locales are surfaced as Cancelled per the cancellation UX spec.
 */
function buildLocaleGroups(
  t: TranslateFn,
  progress: TranslationProgress,
): LocaleStatusGroup[] {
  return [
    {
      label: t('sgd-progress-completed'),
      color: 'green' as const,
      locales: progress.completedLocales,
    },
    {
      label: t('sgd-progress-in-progress'),
      color: 'blue' as const,
      locales: progress.inProgressLocales,
    },
    {
      label: t('sgd-progress-cancelled'),
      color: 'grey' as const,
      locales: progress.cancelledLocales,
    },
    {
      label: t('sgd-progress-failed'),
      color: 'red' as const,
      locales: progress.failedLocales,
    },
  ].filter((group) => group.locales.length > 0);
}

const TranslationProgressPanel: React.FC<TranslationProgressPanelProps> = ({
  progress,
}) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(true);

  const totalLocales =
    progress.completedLocales.length +
    progress.inProgressLocales.length +
    progress.failedLocales.length +
    progress.cancelledLocales.length;

  const percentage =
    totalLocales > 0
      ? Math.round((progress.completedLocales.length / totalLocales) * 100)
      : 0;

  const localeGroups = buildLocaleGroups(t, progress);

  // A count is coloured only when it is non-zero: colour should signal that
  // something actually happened (e.g. a real failure), so "0 Failed" reads as
  // neutral black and only "1+ Failed" turns red. Same for the completed/in-
  // progress counts.
  const statClassName = (count: number, colorClass: string): string =>
    count > 0 ? `${styles.statNumber} ${colorClass}` : styles.statNumber;

  return (
    <Container
      header={
        <div className={styles.progressHeader}>
          <h3 className={styles.progressHeaderTitle}>
            {t('sgd-progress-heading')}
          </h3>
        </div>
      }
    >
      {/* Slim custom progress bar with accessibility */}
      <div
        className={styles.progressBarTrack}
        role="progressbar"
        aria-valuenow={percentage}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={
          // `percentage` is 0-100 for `aria-valuenow`, but the resource uses ICU's
          // `number, percent` style so the sign and its position come from the locale.
          // That style scales its input by 100, hence the division.
          t('sgd-progress-bar-aria', { percentage: percentage / 100 })
        }
      >
        <div
          className={styles.progressBarFill}
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* Percentage label + show details toggle */}
      <div className={styles.progressLabel}>
        <span>{percentage}%</span>
        <button
          className={styles.showDetailsToggle}
          onClick={() => setShowDetails(!showDetails)}
          type="button"
          aria-label={
            showDetails
              ? t('sgd-progress-hide-details-aria')
              : t('sgd-progress-show-details-aria')
          }
          aria-expanded={showDetails}
        >
          {showDetails
            ? t('sgd-progress-hide-details')
            : t('sgd-progress-show-details')}
        </button>
      </div>

      {/* Stats grid */}
      <div className={styles.progressStats}>
        <div className={styles.progressStat}>
          <div
            className={statClassName(
              progress.completedLocales.length,
              styles.statSuccess,
            )}
          >
            {progress.completedLocales.length}
          </div>
          <div className={styles.statLabel}>
            {t('sgd-progress-locales-completed')}
          </div>
        </div>
        <div className={styles.progressStat}>
          <div
            className={statClassName(
              progress.inProgressLocales.length,
              styles.statWarning,
            )}
          >
            {progress.inProgressLocales.length}
          </div>
          <div className={styles.statLabel}>
            {t('sgd-progress-in-progress')}
          </div>
        </div>
        <div className={styles.progressStat}>
          <div className={styles.statNumber}>
            {progress.cancelledLocales.length}
          </div>
          <div className={styles.statLabel}>{t('sgd-progress-cancelled')}</div>
        </div>
        <div className={styles.progressStat}>
          <div
            className={statClassName(
              progress.failedLocales.length,
              styles.statError,
            )}
          >
            {progress.failedLocales.length}
          </div>
          <div className={styles.statLabel}>{t('sgd-progress-failed')}</div>
        </div>
      </div>

      {/* Expandable per-status locale groups. One line per status with its
          locales comma-joined, so a status with many locales stays compact
          instead of producing a long scrolling list of single-locale rows.
          Not `joinList`: these are BCP 47 locale codes, not prose. */}
      {showDetails && localeGroups.length > 0 && (
        <Box margin={{ top: 's' }}>
          <SpaceBetween size="xs">
            {localeGroups.map((group) => (
              <div key={group.label} className={styles.localeGroupRow}>
                <Badge color={group.color}>{group.label}</Badge>
                <span className={styles.localeGroupList}>
                  {group.locales.join(', ')}
                </span>
              </div>
            ))}
          </SpaceBetween>
        </Box>
      )}
    </Container>
  );
};

export default TranslationProgressPanel;
